import React, { useState } from 'react';
import s3Service from '../services/s3Service';

const LaravelImageUpload = ({ onFilesUpload, folder = 'documents' }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Filter only image files
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      alert('Please select only image files.');
      return;
    }

    console.log('📁 Selected image files for upload:', imageFiles.length);
    setUploading(true);

    // Offline entry: keep the raw Blob so the parent can buffer it for sync.
    const asOffline = (file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      blob: file,
      offline: true,
    });

    try {
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

      let uploadedFilesData;
      if (!isOnline) {
        // Offline: don't reach S3 — hand the raw files to the parent to buffer.
        uploadedFilesData = imageFiles.map(asOffline);
      } else {
        uploadedFilesData = await Promise.all(
          imageFiles.map(async (file) => {
            const result = await s3Service.upload(file, folder);
            const s3Url = s3Service.getFileUrl(result.key);
            return {
              id: Date.now() + Math.random(),
              name: file.name,
              size: file.size,
              type: file.type,
              s3Key: result.key,
              s3Url: s3Url,
              blob: file, // retained so a submit-time network failure can buffer
            };
          })
        );
      }

      setUploadedFiles(prev => [...prev, ...uploadedFilesData]);

      // Notify parent component
      if (onFilesUpload) {
        onFilesUpload([...uploadedFiles, ...uploadedFilesData]);
      }

    } catch (error) {
      console.error('❌ Error uploading images:', error);
      // Upload failed (network) — fall back to offline buffering, don't lose them.
      const offlineData = imageFiles.map(asOffline);
      setUploadedFiles(prev => [...prev, ...offlineData]);
      if (onFilesUpload) {
        onFilesUpload([...uploadedFiles, ...offlineData]);
      }
    } finally {
      setUploading(false);
      // Clear the input
      event.target.value = '';
    }
  };

  const removeFile = (fileId) => {
    const updatedFiles = uploadedFiles.filter(file => file.id !== fileId);
    setUploadedFiles(updatedFiles);
    
    // Notify parent component
    if (onFilesUpload) {
      onFilesUpload(updatedFiles);
    }
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
    
    // Notify parent component
    if (onFilesUpload) {
      onFilesUpload([]);
    }
  };


  return (
    <div>
      {/* Laravel-style file input */}
      <div style={{ marginBottom: '10px' }}>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
          style={{
            padding: '6px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#fff',
            fontSize: '14px',
            width: '300px'
          }}
        />
        {uploading && (
          <span style={{ marginLeft: '10px', color: '#666' }}>
            <i className="fa fa-spinner fa-spin"></i> Uploading...
          </span>
        )}
      </div>

      {/* Help text - matching Laravel */}
      <div style={{ 
        fontSize: '12px', 
        color: '#666',
        marginBottom: '10px'
      }}>
        Images (may choose multiple)
      </div>

      {/* Uploaded files list with remove functionality */}
      {uploadedFiles.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          {/* Success header with clear all button */}
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div style={{ 
              color: '#3c763d',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              <i className="glyphicon glyphicon-ok" style={{ marginRight: '5px' }}></i>
              {uploadedFiles.length} image{uploadedFiles.length > 1 ? 's' : ''} uploaded successfully
            </div>
            <button
              type="button"
              onClick={clearAllFiles}
              style={{
                background: '#d9534f',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                padding: '4px 8px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              title="Remove all files"
            >
              Clear All
            </button>
          </div>

          {/* Files list */}
          <div style={{ 
            border: '1px solid #d6e9c6',
            borderRadius: '4px',
            backgroundColor: '#f9f9f9',
            maxHeight: '150px',
            overflowY: 'auto'
          }}>
            {uploadedFiles.map((file, index) => (
              <div key={file.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                borderBottom: index < uploadedFiles.length - 1 ? '1px solid #e0e0e0' : 'none',
                backgroundColor: '#fff'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '13px',
                    color: '#333',
                    marginBottom: '2px'
                  }}>
                    📷 {file.name}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#666'
                  }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(file.id)}
                  style={{
                    background: '#d9534f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    marginLeft: '10px'
                  }}
                  title="Remove this file"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LaravelImageUpload;
